// Shared TanStack QueryClient. The retry policy intentionally only retries
// server-side failures (5xx) — client-side 4xx errors are deterministic and
// retrying them just wastes a network round-trip + delays the error UI.

import { QueryClient } from '@tanstack/vue-query'
import { SERVICE_UNAVAILABLE_ERROR_CODE, type AppError } from '@core/api/normalizeError'

const MAX_RETRIES = 2
const SERVER_ERROR_STATUS_MIN = 500
const ONE_MINUTE_MS = 60_000
const FIVE_MINUTES_MS = 5 * ONE_MINUTE_MS

// A CORS-less gateway 503 has no statusCode, so match it by errorCode too.
const isRetryableError = (err: unknown): boolean => {
  const appError = err as AppError | undefined
  return (
    (appError?.statusCode ?? 0) >= SERVER_ERROR_STATUS_MIN ||
    appError?.errorCode === SERVICE_UNAVAILABLE_ERROR_CODE
  )
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: ONE_MINUTE_MS,
      gcTime: FIVE_MINUTES_MS,
      refetchOnWindowFocus: false,
      retry: (count, err) => count < MAX_RETRIES && isRetryableError(err),
    },
  },
})
